import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import api from "services/api";
import { useStateContext } from "contexts/ContextProvider";
// Guarded: `invalidateValidationCheckConfig` is exported by the parallel
// validation-engine spawn (helpers/serviceValidationChecks.js). Until that
// lands, the import resolves to undefined and the typeof check below keeps
// the save flow working unchanged.
import { invalidateValidationCheckConfig } from "helpers/serviceValidationChecks";
import ValidationCheckNewEdit from "./ValidationCheckNewEdit";

/**
 * Settings → Validation Checks tab.
 *
 * Lists the automatic service validation checks — grouped by SCOPE
 * MEMBERSHIP — and lets the team edit their definitions inline: enablement,
 * and — for checks whose Params carry a numeric `tolerance` — the tolerance
 * value. Full definitions (including severity) are edited in the New/Edit
 * overlay.
 *
 * Server routes:
 *   GET    /validationChecks      → [{ id, Code, Name, Description, ServiceCategory, Severity, IsEnabled, Params, SortOrder, IsBuiltIn, AppliesLogic, FailsLogic, Conditions }]
 *          ServiceCategory is a CSV scope in canonical order
 *          ("Interpretation,Translation,OtherService"; legacy "All" = all
 *          three) — checks covering all three services are collected in an
 *          "All Services" group; every other multi-service check is listed
 *          under EACH service it applies to.
 *   POST   /validationChecks      → create a custom check (Code generated server-side)
 *   PUT    /validationChecks/:id  → update metadata + conditions (customs AND built-ins; code-backed built-ins use only Applies)
 *   DELETE /validationChecks/:id  → custom checks only (built-ins answer 400)
 *
 * The full editor is the ValidationCheckNewEdit overlay, opened from the
 * "New Validation Check" button or a row's Edit action via tab-local state
 * (Settings does not drive these checks through the global overlay
 * registry). The inline controls stay for quick enabled / tolerance
 * edits.
 *
 * The backing `validation_checks` table is created by a migration that has
 * NOT run yet — until it does, GET fails and the tab must show a friendly
 * "not set up yet" empty state instead of crashing.
 *
 * Save flow: optimistic-on-change (matching ReminderWatchSettings toggles).
 * Each control PUTs its field immediately, the row shows a small spinner
 * while saving, and nothing changes locally until the server confirms — so
 * a failed save visually reverts on its own and surfaces an error snackbar.
 */

// Fixed display order for the known service categories inside the tab, with
// their storage tokens. Anything that doesn't parse to a known token falls
// back to one "Uncategorized" group appended after these.
const CATEGORY_GROUPS = [
  { token: "Interpretation", label: "Interpretation" },
  { token: "Translation", label: "Translation" },
  { token: "OtherService", label: "Other Service" },
];

const LABEL_BY_TOKEN = CATEGORY_GROUPS.reduce((map, group) => {
  map[group.token] = group.label;
  return map;
}, {});

// Canonical CSV scope parser shared with the overlay + server: tokens are
// `Interpretation`, `Translation`, `OtherService` — matching is
// case-insensitive and treats `OtherService` and "Other Service" the same.
// Legacy "All" = all three; blank/unknown parses to [] (→ Uncategorized).
const parseCheckScope = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return [];
  if (raw.toLowerCase() === "all") {
    return CATEGORY_GROUPS.map((group) => group.token);
  }
  const seen = new Set();
  raw.split(",").forEach((part) => {
    const norm = part.trim().toLowerCase().replace(/[\s_-]+/g, "");
    if (norm === "interpretation") seen.add("Interpretation");
    else if (norm === "translation") seen.add("Translation");
    else if (norm === "otherservice") seen.add("OtherService");
  });
  return CATEGORY_GROUPS.filter((group) => seen.has(group.token)).map(
    (group) => group.token,
  );
};

// Scope badge for a row: null for single-service checks (the group heading
// already says which service), "All Services" for all three, joined labels
// otherwise (e.g. "Translation + Other Service").
const getScopeBadge = (row) => {
  const scope = parseCheckScope(row?.ServiceCategory);
  if (scope.length < 2) return null;
  if (scope.length === CATEGORY_GROUPS.length) return "All Services";
  return scope.map((token) => LABEL_BY_TOKEN[token]).join(" + ");
};

// Server may return Params as a JSON object (JSON column) or a JSON string
// (TEXT column) depending on the migration. Never throws.
const parseParams = (params) => {
  if (!params) return null;
  if (typeof params === "object" && !Array.isArray(params)) return params;
  if (typeof params === "string") {
    try {
      const parsed = JSON.parse(params);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : null;
    } catch (error) {
      return null;
    }
  }
  return null;
};

// Numeric tolerance from a row's Params, or null when absent/invalid.
// DECIMAL strings ("0.05") are coerced with Number(), mirroring the engine.
const getTolerance = (row) => {
  const params = parseParams(row?.Params);
  if (!params) return null;
  const raw = params.tolerance;
  if (raw === null || raw === undefined || raw === "") return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

// Sequelize TINYINT/BOOLEAN can arrive as true/1/"1"/"true".
const isEnabledFlag = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

// Built-in checks ship with the Portal code — their metadata is editable but
// their conditions are not, and they can't be deleted (disable instead).
const isBuiltInCheck = (row) => isEnabledFlag(row?.IsBuiltIn);

const ValidationChecksTab = ({ theme, isMobile }) => {
  const { showSnackbar, openDialog, closeDialog } = useStateContext();

  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  // Tab-local overlay state: null = closed, { mode: "create" } or
  // { mode: "edit", check: row }. Settings renders the New/Edit overlay
  // directly instead of going through the global overlay registry.
  const [editorState, setEditorState] = useState(null);
  // Local drafts for the tolerance inputs, keyed by row id. Committed on
  // blur / Enter; cleared back to the stored value on commit or failure.
  const [toleranceDrafts, setToleranceDrafts] = useState({});

  // `silent` refreshes (after an overlay save/delete) skip the loading
  // spinner and keep the current rows when the refetch fails.
  const fetchChecks = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const response = await api.get("/validationChecks");
      const rows = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.checks)
          ? response.data.checks
          : [];
      setChecks(rows);
      setToleranceDrafts({});
    } catch (error) {
      // Pre-migration the endpoint 404s/500s. Render the friendly empty
      // state below instead of a snackbar — a snackbar here would fire on
      // every visit to the tab until Amine runs the migration.
      console.error("Error fetching validation checks:", error);
      if (!silent) setChecks([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Fetch ONCE per mount — fetchChecks has a stable identity (no deps).
  useEffect(() => {
    fetchChecks();
  }, [fetchChecks]);

  const clearToleranceDraft = useCallback((rowId) => {
    setToleranceDrafts((prev) => {
      if (prev[rowId] === undefined) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  }, []);

  // Tell the validation engine its cached definitions changed. The export is
  // owned by the parallel spawn — guarded for the window before it lands.
  const notifyConfigChanged = useCallback(() => {
    try {
      if (typeof invalidateValidationCheckConfig === "function") {
        invalidateValidationCheckConfig();
      }
    } catch (error) {
      console.error("Error invalidating validation check config:", error);
    }
  }, []);

  const saveRow = useCallback(
    async (row, patch, successMessage = "Check updated") => {
      if (savingId === row.id) return;
      setSavingId(row.id);
      try {
        const response = await api.put(`/validationChecks/${row.id}`, patch);
        const serverRow =
          response?.data &&
          typeof response.data === "object" &&
          !Array.isArray(response.data)
            ? response.data
            : null;
        setChecks((prev) =>
          prev.map((item) => {
            if (item.id !== row.id) return item;
            if (serverRow && (serverRow.Code !== undefined || serverRow.id !== undefined)) {
              return { ...item, ...serverRow };
            }
            return { ...item, ...patch };
          }),
        );
        showSnackbar(successMessage, theme, "success");
        notifyConfigChanged();
      } catch (error) {
        console.error("Error updating validation check:", error);
        showSnackbar(
          error?.response?.data?.error || "Error updating check",
          theme,
          "error",
        );
      } finally {
        setSavingId(null);
      }
    },
    [notifyConfigChanged, savingId, showSnackbar, theme],
  );

  const handleToggleEnabled = (row) => {
    void saveRow(row, { IsEnabled: !isEnabledFlag(row.IsEnabled) });
  };

  const handleToleranceCommit = (row) => {
    // Blur can fire again after Enter while the PUT is still in flight.
    if (savingId === row.id) return;

    const draft = toleranceDrafts[row.id];
    const raw = draft === undefined || draft === null ? "" : String(draft).trim();
    const storedTolerance = getTolerance(row);

    if (raw === "") {
      // Blank — ignore, just snap back to the stored value.
      clearToleranceDraft(row.id);
      return;
    }

    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) {
      showSnackbar("Tolerance must be a number of 0 or more", theme, "error");
      clearToleranceDraft(row.id);
      return;
    }

    if (storedTolerance !== null && parsed === storedTolerance) {
      clearToleranceDraft(row.id);
      return;
    }

    void saveRow(row, { Params: { tolerance: parsed } }, "Check updated").then(() => {
      clearToleranceDraft(row.id);
    });
  };

  // Row Delete (custom checks only) — confirm dialog, then DELETE, then a
  // SILENT refetch so the list behind the dialog never flashes a spinner.
  const handleDeleteCheck = (row) => {
    openDialog({
      theme,
      title: "Delete Validation Check",
      message: `Delete "${row.Name}"? This cannot be undone.`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        try {
          await api.delete(`/validationChecks/${row.id}`);
          showSnackbar("Check deleted", theme, "success");
          notifyConfigChanged();
          closeDialog();
          fetchChecks(true);
        } catch (error) {
          console.error("Error deleting validation check:", error);
          closeDialog();
          showSnackbar(
            error?.response?.data?.error || "Error deleting check",
            theme,
            "error",
          );
        }
      },
      secondaryOnClick: closeDialog,
    });
  };

  const renderRow = (row, group) => {
    const isSaving = savingId === row.id;
    const enabled = isEnabledFlag(row.IsEnabled);
    const builtIn = isBuiltInCheck(row);
    // Rows inside the "All Services" group are, by definition, all-three
    // checks — the group heading already says it, so the (redundant) badge
    // is suppressed there. Other groups keep the joined-label badge for
    // 2-of-3 checks; single-service rows have no badge either way.
    const scopeBadge =
      group?.key === "allServices" ? null : getScopeBadge(row);
    const tolerance = getTolerance(row);
    const showsTolerance = tolerance !== null;
    const draftTolerance = toleranceDrafts[row.id];
    const toleranceValue =
      draftTolerance !== undefined ? draftTolerance : tolerance ?? "";

    return (
      <Box
        key={row.id ?? row.Code}
        sx={{
          p: 1.25,
          borderRadius: "8px",
          border: `1px solid ${theme.palette.primary.border2}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              minWidth: 0,
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              {row.Name}
            </Typography>
            {scopeBadge ? (
              <Typography
                variant="caption"
                sx={{
                  px: 0.75,
                  py: 0.125,
                  borderRadius: "6px",
                  border: "1px solid",
                  borderColor: theme.palette.primary.border2,
                  color: "text.secondary",
                  fontWeight: 600,
                  lineHeight: 1.6,
                  whiteSpace: "nowrap",
                }}
              >
                {scopeBadge}
              </Typography>
            ) : null}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", fontFamily: "monospace" }}
          >
            {row.Code}
          </Typography>
          {row.Description ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25 }}
            >
              {row.Description}
            </Typography>
          ) : null}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          {showsTolerance && (
            <TextField
              label="Tolerance"
              size="small"
              type="number"
              value={toleranceValue}
              onChange={(event) =>
                setToleranceDrafts((prev) => ({
                  ...prev,
                  [row.id]: event.target.value,
                }))
              }
              onBlur={() => handleToleranceCommit(row)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleToleranceCommit(row);
                }
              }}
              disabled={isSaving}
              inputProps={{ min: 0, step: "0.01" }}
              sx={{ width: isMobile ? "100%" : 120 }}
            />
          )}

          <Switch
            checked={enabled}
            onChange={() => handleToggleEnabled(row)}
            disabled={isSaving}
            inputProps={{ "aria-label": `${enabled ? "Disable" : "Enable"} ${row.Name}` }}
          />

          <Tooltip title="Edit check" placement="top">
            <IconButton
              size="small"
              onClick={() => setEditorState({ mode: "edit", check: row })}
              disabled={isSaving}
              aria-label={`Edit ${row.Name}`}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip
            placement="top"
            title={
              builtIn
                ? "Built-in checks can be disabled, not deleted"
                : "Delete check"
            }
          >
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteCheck(row)}
                disabled={isSaving || builtIn}
                aria-label={`Delete ${row.Name}`}
              >
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {isSaving && <CircularProgress size={16} />}
        </Box>
      </Box>
    );
  };

  // Group rows by SCOPE MEMBERSHIP: checks scoped to ALL THREE services are
  // collected in their own "All Services" group at the top; every other
  // check appears under EACH service its CSV scope includes, so counts are
  // membership counts (a multi-service check counts in each group —
  // intended). Rows whose scope doesn't match any known service fall back
  // to one "Uncategorized" group, appended last. Row order within a group
  // is preserved as returned by the server (SortOrder).
  const groupedChecks = (() => {
    const allServices = {
      key: "allServices",
      name: "All Services",
      rows: [],
    };
    const groups = CATEGORY_GROUPS.map((category) => ({
      key: category.token,
      name: category.label,
      rows: [],
    }));
    const uncategorized = {
      key: "uncategorized",
      name: "Uncategorized",
      rows: [],
    };
    checks.forEach((row) => {
      const scope = parseCheckScope(row?.ServiceCategory);
      if (scope.length === 0) {
        uncategorized.rows.push(row);
        return;
      }
      // All three services → the dedicated "All Services" group ONLY (no
      // longer repeated under each category).
      if (scope.length === CATEGORY_GROUPS.length) {
        allServices.rows.push(row);
        return;
      }
      scope.forEach((token) => {
        const group = groups.find((item) => item.key === token);
        if (group) group.rows.push(row);
      });
    });
    const ordered = [allServices, ...groups];
    if (uncategorized.rows.length > 0) ordered.push(uncategorized);
    return ordered;
  })();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold">
            Validation Checks
          </Typography>
          <IconButton
            aria-label="Add validation check"
            onClick={() => setEditorState({ mode: "create" })}
            sx={{
              height: 36,
              width: 36,
              p: 0,
              borderRadius: "12px",
              backgroundColor: theme.palette.background.default,
              border: "1px solid",
              borderColor: theme.palette.divider,
              color: theme.palette.primary.main,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              "&:hover": {
                borderColor: theme.palette.primary.main,
              },
              "&:focus-visible": {
                borderColor: theme.palette.primary.main,
                outline: "none",
              },
            }}
          >
            <AddRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          Automatic checks run against stored service rows — interpretations,
          translations, and other services — and flag pricing, mileage and
          completeness issues. Checks created for every service are collected
          in the All Services group; every other check is listed under each
          service it applies to, with a badge marking the ones that cover
          more than one service. Enable or disable a check, or fine-tune its
          tolerance inline — or open a check for its full definition.
          Changes apply the next time services are evaluated.
        </Typography>
        <Divider sx={{ my: 2 }} />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : checks.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
              p: 4,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              No validation checks available
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 440 }}
            >
              Validation checks aren't set up yet. Run the database migration to
              seed the check definitions.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => fetchChecks()}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
              }}
            >
              Retry
            </Button>
          </Box>
        ) : (
          groupedChecks.map((group) => (
            <Box
              key={group.name}
              sx={{ display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {group.name} ({group.rows.length})
                </Typography>
                <Divider sx={{ mt: 0.5 }} />
              </Box>
              {group.rows.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 0.5, px: 0.25 }}
                >
                  No checks in this category yet — create one with the + button
                  above.
                </Typography>
              ) : (
                group.rows.map((row) => renderRow(row, group))
              )}
            </Box>
          ))
        )}
      </Box>

      {editorState && (
        <ValidationCheckNewEdit
          check={editorState.mode === "edit" ? editorState.check : null}
          onClose={() => setEditorState(null)}
          onSaved={() => fetchChecks(true)}
        />
      )}
    </Box>
  );
};

export default ValidationChecksTab;
